import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle2, Circle, ChevronRight, X, Rocket, ExternalLink, Lock, PartyPopper } from 'lucide-react';
import { carsysApi } from '../api/carsysApi';
import { useT } from '../context/LanguageContext';
import { buildStepMeta, openSettingsTab } from '../lib/onboardingSteps';

export const OnboardingChecklist = ({ onNavigate }) => {
  const { t } = useT();
  const [status, setStatus] = useState(null);

  const load = useCallback(() => { carsysApi.getOnboardingStatus().then(setStatus).catch(() => setStatus(null)); }, []);
  useEffect(() => { load(); }, [load]);

  if (!status || status.dismissed || status.applicableTotal === 0) return null;

  // Eenmalige "Setup compleet"-bevestiging zodra alles voltooid/uitgeschakeld is.
  if (status.allDone) {
    if (status.celebrated) return null;
    const seen = async () => {
      try { await carsysApi.markOnboardingCelebrated(); } catch (_) { /* noop */ }
      setStatus({ ...status, celebrated: true });
    };
    return (
      <div data-testid="onboarding-complete" style={doneWrap}>
        <div style={doneIcon}><PartyPopper size={20} /></div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 15.5, color: '#065f46' }}>{t('Setup compleet 🎉', 'Setup compleet 🎉')}</div>
          <div style={{ fontSize: 13, color: '#047857' }}>{t('Je garage is helemaal ingesteld. Veel succes met MonteurCentraal!', 'Je garage is helemaal ingesteld. Veel succes met MonteurCentraal!')}</div>
        </div>
        <button style={doneClose} data-testid="onboarding-complete-dismiss" onClick={seen} title={t('Sluiten', 'Sluiten')}><X size={16} /></button>
      </div>
    );
  }

  const meta = buildStepMeta(t);
  const applicable = status.steps.filter(s => s.applicable);
  const pct = Math.round((status.applicableDone / status.applicableTotal) * 100);

  const dismiss = async () => { try { await carsysApi.dismissOnboarding(); } catch (_) { /* noop */ } setStatus({ ...status, dismissed: true }); };

  const SKIP_KEY = { betalen: 'stripe', twilio: 'twilio', afspraken: 'afspraken' };
  const toggleSkip = async (stepKey, skip) => {
    const sk = SKIP_KEY[stepKey]; if (!sk) return;
    try { await carsysApi.skipIntegration(sk, skip); } catch (_) { /* noop */ }
    load();
  };

  return (
    <div data-testid="onboarding-checklist" style={wrap}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div style={iconBadge}><Rocket size={18} /></div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 16, color: '#0f172a' }}>{t('Aan de slag met MonteurCentraal', 'Aan de slag met MonteurCentraal')}</div>
          <div style={{ fontSize: 13, color: '#64748b' }}>{status.applicableDone}/{status.applicableTotal} {t('stappen voltooid', 'stappen voltooid')}</div>
        </div>
        <button style={closeBtn} data-testid="onboarding-dismiss" onClick={dismiss} title={t('Verbergen', 'Verbergen')}><X size={16} /></button>
      </div>
      <div style={barTrack}><div style={{ ...barFill, width: `${pct}%` }} /></div>
      <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>
        {applicable.map(s => {
          const m = meta[s.key]; if (!m) return null;
          const Icon = m.icon;
          return (
            <div key={s.key} data-testid={`onboarding-step-${s.key}`} style={{ ...row, opacity: s.done ? 0.7 : 1 }}>
              {s.done ? <CheckCircle2 size={20} color="#16a34a" /> : <Circle size={20} color="#cbd5e1" />}
              <Icon size={16} color="#475569" style={{ flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13.5, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
                  {m.title}
                  {m.proOnly && <span style={proBadge}>PRO</span>}
                  {s.skipped && <span style={offBadge} data-testid={`onboarding-off-${s.key}`}>{t('Uitgeschakeld', 'Uitgeschakeld')}</span>}
                </div>
              </div>
              {s.skipped ? (
                <button style={undoBtn} data-testid={`onboarding-enable-${s.key}`} onClick={() => toggleSkip(s.key, false)}>
                  {t('Toch instellen', 'Toch instellen')}
                </button>
              ) : !s.done ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  {s.skippable && (
                    <button style={skipBtn} data-testid={`onboarding-skip-${s.key}`} onClick={() => toggleSkip(s.key, true)}>
                      {t('Niet gebruiken', 'Niet gebruiken')}
                    </button>
                  )}
                  <button style={goBtn} data-testid={`onboarding-go-${s.key}`} onClick={() => m.nav(onNavigate)}>
                    {t('Instellen', 'Instellen')} <ChevronRight size={14} />
                  </button>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      {!status.licensed && (
        <div style={teaser} data-testid="onboarding-pro-teaser">
          <Lock size={13} /> {t('Met PRO ontgrendel je o.a. SMS/WhatsApp-herinneringen en online afspraken.', 'Met PRO ontgrendel je o.a. SMS/WhatsApp-herinneringen en online afspraken.')}
          <button style={teaserLink} data-testid="onboarding-upgrade" onClick={() => openSettingsTab(onNavigate, 'plannen')}>{t('Bekijk PRO', 'Bekijk PRO')} <ExternalLink size={12} /></button>
        </div>
      )}
      <button style={guideLink} data-testid="onboarding-open-guide" onClick={() => onNavigate('aandeslag')}>{t('Volledige setup-gids openen', 'Volledige setup-gids openen')} <ChevronRight size={13} /></button>
    </div>
  );
};

const wrap = { background: 'linear-gradient(135deg,#eff6ff,#ffffff)', border: '1px solid #bfdbfe', borderRadius: 14, padding: 18, marginBottom: 20, boxShadow: '0 4px 14px rgba(37,99,235,.08)' };
const doneWrap = { display: 'flex', alignItems: 'center', gap: 12, background: 'linear-gradient(135deg,#ecfdf5,#ffffff)', border: '1px solid #a7f3d0', borderRadius: 14, padding: '14px 16px', marginBottom: 20, boxShadow: '0 4px 14px rgba(16,185,129,.10)' };
const doneIcon = { width: 38, height: 38, borderRadius: 10, background: '#10b981', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 };
const doneClose = { background: 'transparent', border: 'none', color: '#059669', cursor: 'pointer', padding: 4, flexShrink: 0 };
const iconBadge = { width: 38, height: 38, borderRadius: 10, background: '#2563eb', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 };
const closeBtn = { background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: 4 };
const barTrack = { height: 7, background: '#dbeafe', borderRadius: 99, overflow: 'hidden' };
const barFill = { height: '100%', background: '#2563eb', borderRadius: 99, transition: 'width 300ms ease' };
const row = { display: 'flex', alignItems: 'center', gap: 10, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '9px 12px' };
const goBtn = { display: 'inline-flex', alignItems: 'center', gap: 3, padding: '6px 12px', border: 'none', borderRadius: 7, background: '#2563eb', color: '#fff', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', flexShrink: 0 };
const skipBtn = { padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 7, background: '#fff', color: '#64748b', fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' };
const undoBtn = { padding: '6px 10px', border: '1px solid #bfdbfe', borderRadius: 7, background: '#eff6ff', color: '#2563eb', fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' };
const offBadge = { fontSize: 9.5, fontWeight: 800, background: '#f1f5f9', color: '#475569', padding: '1px 6px', borderRadius: 99, letterSpacing: 0.3 };
const proBadge = { fontSize: 9.5, fontWeight: 800, background: '#fef3c7', color: '#92400e', padding: '1px 6px', borderRadius: 99, letterSpacing: 0.4 };
const teaser = { marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12.5, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 9, padding: '8px 12px' };
const teaserLink = { marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4, background: 'transparent', border: 'none', color: '#b45309', fontWeight: 700, cursor: 'pointer', fontSize: 12.5 };
const guideLink = { marginTop: 12, display: 'inline-flex', alignItems: 'center', gap: 3, background: 'transparent', border: 'none', color: '#2563eb', fontWeight: 700, cursor: 'pointer', fontSize: 13 };
